import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { createNotification } from '@/hooks/useNotifications';

export type ChatMessage = {
  id: string;
  match_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
};

export type ChatPartner = {
  name: string;
  phone: string;
  role: 'patient' | 'caregiver';
};

export type MatchInfo = {
  match_number: string | null;
  start_date: string | null;
  end_date: string | null;
  location: string | null;
  hourly_rate: number | null;
  status: string;
};

// 연락처/메신저 공유 시도 패턴 감지
const CONTACT_PATTERNS = [
  /\b01[016789][-\s.]?\d{3,4}[-\s.]?\d{4}\b/,       // 한국 휴대폰 번호
  /\b0\d{1,2}[-\s.]?\d{3,4}[-\s.]?\d{4}\b/,          // 유선전화
  /\bkakao\s*talk\b/i,                                 // 카카오톡
  /\b카톡\b/i,                                         // 카톡 (한글)
  /\b카카오톡\b/i,                                      // 카카오톡 (한글)
  /\btelegram\b/i,                                      // 텔레그램
  /\b텔레그램\b/i,                                       // 텔레그램 (한글)
  /\bline\b/i,                                          // 라인
  /\b라인\b/i,                                          // 라인 (한글)
  /\binstagram\b/i,                                     // 인스타
  /\b인스타\b/i,                                         // 인스타 (한글)
  /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,  // 이메일
  /\b(id|아이디|계정)\s*[:=]?\s*\S+/i,                  // 아이디 공유
];

export function detectContactSharing(text: string): { detected: boolean; pattern: string } {
  for (const pattern of CONTACT_PATTERNS) {
    const match = text.match(pattern);
    if (match) {
      return { detected: true, pattern: match[0] };
    }
  }
  return { detected: false, pattern: '' };
}

export function useChatMessages(matchId: string | undefined, userId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [partner, setPartner] = useState<ChatPartner | null>(null);
  const [matchInfo, setMatchInfo] = useState<MatchInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const loadedRef = useRef(false);
  const partnerIdRef = useRef<string | null>(null);
  const myNameRef = useRef<string>('');

  // 채팅 상대방 정보 + 메시지 불러오기
  const loadChat = useCallback(async () => {
    if (!matchId || !userId) return;
    setLoading(true);
    setError('');

    try {
      // 1. 매칭 정보 확인 + 상대방 정보 가져오기
      const { data: match, error: matchError } = await supabase
        .from('match_requests')
        .select('*')
        .eq('id', matchId)
        .in('status', ['accepted', 'completed'])
        .maybeSingle();

      if (matchError) throw matchError;
      if (!match) {
        setError('채팅할 수 없는 매칭입니다.');
        setLoading(false);
        return;
      }

      // 접근 권한 체크
      if (match.requester_id !== userId && match.receiver_id !== userId) {
        setError('접근 권한이 없습니다.');
        setLoading(false);
        return;
      }

      // 매칭 정보 저장
      setMatchInfo({
        match_number: match.match_number || null,
        start_date: match.start_date || null,
        end_date: match.end_date || null,
        location: match.location || null,
        hourly_rate: match.hourly_rate || null,
        status: match.status,
      });

      // 상대방 정보
      const partnerUserId = match.requester_id === userId ? match.receiver_id : match.requester_id;
      const partnerRole = match.requester_id === userId ? match.receiver_role : match.requester_role;

      // 나중에 메시지 전송 시 재사용할 partnerId 저장
      partnerIdRef.current = partnerUserId;

      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('name, phone')
        .eq('id', partnerUserId)
        .maybeSingle();

      // 내 이름도 저장 (알림 생성 시 사용)
      const { data: myProfile } = await supabase
        .from('profiles')
        .select('name')
        .eq('id', userId)
        .maybeSingle();
      myNameRef.current = myProfile?.name || '';

      setPartner({
        name: partnerProfile?.name || '알 수 없음',
        phone: match.contact_revealed ? (partnerProfile?.phone || '') : '',
        role: partnerRole as 'patient' | 'caregiver',
      });

      // 2. 기존 메시지 불러오기
      const { data: existingMessages, error: msgError } = await supabase
        .from('messages')
        .select('*')
        .eq('match_id', matchId)
        .order('created_at', { ascending: true })
        .limit(200);

      if (msgError) throw msgError;
      setMessages((existingMessages || []) as ChatMessage[]);

      // 3. 상대방이 보낸 메시지 읽음 처리
      const unreadIds = (existingMessages || [])
        .filter((m) => m.sender_id !== userId && !m.is_read)
        .map((m) => m.id);

      if (unreadIds.length > 0) {
        await supabase
          .from('messages')
          .update({ is_read: true })
          .in('id', unreadIds);
      }
    } catch (err) {
      console.error('Failed to load chat:', err);
      setError('채팅을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [matchId, userId]);

  // 실시간 구독
  useEffect(() => {
    if (!matchId || !userId) return;

    // 중복 구독 방지
    if (loadedRef.current) return;
    loadedRef.current = true;

    loadChat();

    // Supabase Realtime 구독
    const channel = supabase
      .channel(`chat-${matchId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `match_id=eq.${matchId}`,
        },
        (payload) => {
          const newMsg = payload.new as ChatMessage;
          setMessages((prev) => {
            // 중복 방지
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });

          // 상대방이 보낸 메시지면 자동 읽음 처리
          if (newMsg.sender_id !== userId) {
            supabase
              .from('messages')
              .update({ is_read: true })
              .eq('id', newMsg.id);
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
      loadedRef.current = false;
    };
  }, [matchId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 메시지 전송
  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    if (!matchId || !userId || !partner) return false;

    const trimmed = content.trim();
    if (!trimmed) return false;

    // 연락처 공유 감지
    const contactCheck = detectContactSharing(trimmed);
    if (contactCheck.detected) {
      return false; // 연락처 공유 차단 - 호출 측에서 처리
    }

    setSending(true);
    try {
      const { data, error: sendError } = await supabase
        .from('messages')
        .insert({
          match_id: matchId,
          sender_id: userId,
          content: trimmed,
        })
        .select()
        .single();

      if (sendError) throw sendError;

      // 상대방에게 알림 생성 (비동기)
      const partnerId = partnerIdRef.current;
      if (partnerId) {
        const myName = myNameRef.current || '상대방';
        createNotification({
          userId: partnerId,
          type: 'chat_message',
          title: `${myName}님의 새 메시지`,
          body: trimmed.length > 50 ? trimmed.slice(0, 50) + '...' : trimmed,
          relatedId: matchId,
        });
      }

      return true;
    } catch (err) {
      console.error('Failed to send message:', err);
      return false;
    } finally {
      setSending(false);
    }
  }, [matchId, userId, partner]);

  return {
    messages,
    partner,
    matchInfo,
    loading,
    error,
    sending,
    sendMessage,
  };
}